/**
 * Cross-feature trigger surface for the Attach-Invoice modal.
 *
 * Architecture mirrors the Modal Manager pattern introduced by the
 * transaction-modal-store (see `./transaction-modal-store.ts`):
 *   - The MODAL component (`AttachInvoiceModal`) lives in
 *     `features/accounting/` where its domain logic belongs — Invoice +
 *     Transaction types, mutation hooks, payment-method taxonomy.
 *   - This STORE lives in `shared/` and carries only the open/close state
 *     plus a thin opaque payload. Any feature can dispatch to it.
 *   - The `AttachInvoiceModalRoot` widget (in
 *     `widgets/attach-invoice-modal-root/`) subscribes here and renders
 *     `<AttachInvoiceModal>` once globally, intercepting the save-success
 *     callback to fire caller-specified query invalidations.
 *
 * Why a parallel store + widget instead of multiplexing through the
 * existing transaction-modal-store:
 *   - The two modals have different payload contracts (`TxnFormMode` vs
 *     `AttachInvoiceLaunchPayload`). A discriminator-based mux would force
 *     callers to thread the wrong-shaped payload through a single open()
 *     and then the widget would dispatch internally — extra indirection,
 *     no upside. Two stores keep each call-site type-safe at the helper.
 *   - The patterns are literally identical, so future devs can copy this
 *     file to bootstrap any new cross-feature modal.
 *
 * Re-fetch contract (two layers — same as transaction-modal-store):
 *   1. CANONICAL — accounting's pending-payments / transactions / invoices /
 *      dashboard caches. Handled INSIDE `useCreateInvoice` via
 *      `useEntityInvalidate(ACCOUNTING_PREFIX)`. Fires regardless of who
 *      opened the modal. Callers don't have to opt in.
 *   2. CALLER-SPECIFIED — derived queries the calling page owns. Passed via
 *      `invalidateOnSuccess` per-open. The widget invalidates them after
 *      the mutation succeeds, before invoking the per-open `onSuccess`.
 *
 * @example PendingPaymentsPage bulk button
 * ```tsx
 * import { openAttachInvoice } from '@/shared/stores'
 *
 * <Button
 *   disabled={selection[tab].length === 0}
 *   onClick={() =>
 *     openAttachInvoice({
 *       kind: tab === 'EXPENSES' ? 'EXPENSE' : 'INCOME',
 *       txnIds: selection[tab],
 *     })
 *   }
 * >…</Button>
 * ```
 */

import { create } from 'zustand'
import { useShallow } from 'zustand/react/shallow'

/** EXPENSE → payment-out (PAY-*); INCOME → payment-in (RCT-*). */
export type AttachInvoiceKind = 'EXPENSE' | 'INCOME'

/**
 * Launch shape — what every caller hands the store. `kind` is mandatory so
 * the modal opens with the right type pre-selected; `txnIds` (when present)
 * seeds the covered-txn selector; `mode` opts into the LINK flow for
 * InvoiceDetail callers (default: CREATE). The store keeps it serialisable
 * (no functions, no class instances) so future tests can JSON-fixture it.
 */
export interface AttachInvoiceLaunchPayload {
  kind: AttachInvoiceKind
  txnIds?: readonly string[]
  /** CREATE opens a new PENDING invoice; LINK appends to an existing one. */
  mode?: 'CREATE' | 'LINK'
  /** Only consulted when `mode === 'LINK'`. */
  invoiceId?: string
}

interface AttachInvoiceModalState {
  isOpen: boolean
  payload: AttachInvoiceStorePayload | null
  open: (payload: AttachInvoiceStorePayload) => void
  close: () => void
}

interface AttachInvoiceStorePayload {
  /**
   * Structurally an `AttachInvoiceLaunchPayload`. Kept opaque to respect
   * the `shared → features` FSD constraint; the modal-root widget casts at
   * the render boundary.
   */
  launch: unknown
  /** Query-key prefixes to invalidate after a successful save. */
  invalidateOnSuccess?: ReadonlyArray<string | readonly string[]>
  /** Called after canonical + caller-specified invalidations run. */
  onSuccess?: () => void
}

export const useAttachInvoiceModalStore = create<AttachInvoiceModalState>((set) => ({
  isOpen: false,
  payload: null,
  open: (payload) => set({ isOpen: true, payload }),
  close: () => set({ isOpen: false, payload: null }),
}))

/**
 * Selector hook for components that need to react to the modal state
 * (typically only `AttachInvoiceModalRoot`). Returns a shallow-equal slice
 * so unrelated store updates don't cause re-renders.
 */
export function useAttachInvoiceModal() {
  return useAttachInvoiceModalStore(
    useShallow((s) => ({
      isOpen: s.isOpen,
      payload: s.payload,
      open: s.open,
      close: s.close,
    })),
  )
}

interface OpenOpts {
  invalidateOnSuccess?: ReadonlyArray<string | readonly string[]>
  onSuccess?: () => void
}

/**
 * Open the Attach-Invoice modal. Usable from any feature.
 *
 * @example Pending-payments bulk attach (EXPENSES)
 *   openAttachInvoice({ kind: 'EXPENSE', txnIds: ['T-2605-001'] })
 *
 * @example Invoice detail page link mode (future)
 *   openAttachInvoice({ kind: 'EXPENSE', mode: 'LINK', invoiceId: 'PAY-2605-003' })
 */
export function openAttachInvoice(launch: AttachInvoiceLaunchPayload, opts?: OpenOpts) {
  useAttachInvoiceModalStore.getState().open({
    launch,
    invalidateOnSuccess: opts?.invalidateOnSuccess,
    onSuccess: opts?.onSuccess,
  })
}

/** Imperative close — exposed for non-React callers (cancellation, tests). */
export function closeAttachInvoiceModal() {
  useAttachInvoiceModalStore.getState().close()
}
